import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Radio, Tooltip } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { FormattedMessage, useIntl } from 'react-intl';

import './styles.scss';
import {
  PAYMENT_TYPE as CAMPAIGN_PAYMENT_TYPE,
  PAYMENT_METHOD_TYPE,
  USER_LEVEL_TYPE,
  TRANSACTION_TYPE,
  BALANCE_MODAL_TYPE,
  DEFAULT_BALANCE_OPTIONS_CHECKOUT,
  USER_ROLE_TYPES,
  QUALIFICATION_STATUS,
  QUALIFICATION_STATUS_LABEL
} from 'constants/common';
import useProfile from 'hooks/useProfile';
import { useGetListPaymentMethod, useGetTermQualificationStatus, useGenerateTermQualificationLink } from 'hooks/usePayment';
import useBalanceCheckout from 'hooks/useBalanceCheckout';
import { formatNumber } from 'utilities/stringHelper';
import BalanceModal from '../BalanceModal';
import { MyInput, MyButton } from 'components/Atoms';
import ModalTopUp from 'components/Pages/Wallet/components/WalletTransaction/components/ModalTopUp';
import ModalNewCard from 'components/Pages/Wallet/components/ModalNewCard';
import AppliedCodeSection from 'components/Organisms/AppliedDiscountCode';
import ModalRequestFund from 'components/Pages/Wallet/components/WalletTransaction/components/ModalRequestFund';
import { calculateTotalPaymentWithTermsSurcharge } from 'utilities/numberHelper';

const PaymentInfo = ({
  _onPaymentTypeChange,
  _onPaymentMethodChange,
  disabled,
  transactionType = TRANSACTION_TYPE.TEMPLATE_PAYMENT,
  codeData,
  codeError,
  handleChangeParams,
  shouldShowRequestFunds = false,
  total = 0,
}) => {
  const intl = useIntl();
  const [formTopup] = useForm();
  const codeRef = useRef(null);
  const [paymentType, setPaymentType] = useState(CAMPAIGN_PAYMENT_TYPE.ACCOUNT_BALANCE);
  const [paymentMethodId, setPaymentMethodId] = useState(undefined);
  const [visibleTopup, setVisibleTopup] = useState(false);
  const [visibleNewCard, setVisibleNewCard] = useState(false);
  const [visibleRequestFunds, setVisibleRequestFunds] = useState(false);
  const [totalPayment, setTotalPayment] = useState(total);

  const { data: paymentMethods } = useGetListPaymentMethod();
  const { data: userInfo } = useProfile();
  const { data: termQualification } = useGetTermQualificationStatus({ enabled : (userInfo?.role === USER_ROLE_TYPES.AGENCY && userInfo?.level === USER_LEVEL_TYPE.ADMIN) });
  const { data: termQualificationLink } = useGenerateTermQualificationLink({ enabled : (userInfo?.role === USER_ROLE_TYPES.AGENCY && userInfo?.level === USER_LEVEL_TYPE.ADMIN) });

  const onPaymentChange = (e) => {
    const paymentType = e.target.value;

    setPaymentType(paymentType);
    typeof _onPaymentTypeChange === 'function' &&
      _onPaymentTypeChange(paymentType);
  };

  const onPaymentMethodChange = (id) => {
    setPaymentMethodId(id);
    typeof _onPaymentMethodChange === 'function' && _onPaymentMethodChange(id);
  };

  const onToggleTopup = useCallback(
    (value) => () => {
      setVisibleTopup(value);
    },
    []
  );

  const onToggleNewCard = useCallback(
    (value) => () => {
      setVisibleNewCard(value);
    },
    []
  );
  const onToggleRequestFunds = useCallback(
    (value) => () => {
      setVisibleRequestFunds(value);
    },
    []
  );

  const allowCard =
    userInfo?.level === USER_LEVEL_TYPE.ADMIN ||
    (userInfo?.level === USER_LEVEL_TYPE.CAMPAIGN_MANAGER &&
      userInfo.manageCard);
  const canRequestFunds =
    shouldShowRequestFunds &&
    userInfo?.level === USER_LEVEL_TYPE.CAMPAIGN_MANAGER &&
    !userInfo.manageCard;

  useEffect(() => {
    if (paymentMethods?.length) {
      const paymentMId =
        paymentMethods.filter((el) => el.type === PAYMENT_METHOD_TYPE.CARD)[0]
          ?.id || undefined;
      setPaymentMethodId(paymentMId);
      onPaymentMethodChange(paymentMId);
    }
  }, [paymentMethods]);

  const balanceCheckout = useBalanceCheckout(
    `#${BALANCE_MODAL_TYPE.BALANCE_CHECKOUT_TOPUP}`,
    {
      ...DEFAULT_BALANCE_OPTIONS_CHECKOUT,
      onClose: () => {
        balanceCheckout.setVisible(false);
      },
      onCancel: () => {
        balanceCheckout.setVisible(false);
      },
      onSuccess: () => {
        balanceCheckout.setCheckoutSuccess(true);
      },
      onError: () => {
        balanceCheckout.setVisible(false);
      },
    }
  );

  const handleClickApply = () => {
    const code = codeRef.current?.state.value;
    if (!code) return;

    handleChangeParams({
      code,
    });
  };

  const handleClickRemove = () => {
    if (codeRef.current) {
      codeRef.current.state.value = '';
    }
    handleChangeParams({
      code: '',
    });
  };

  return (
    <div className="border-block payment-info">
      <span className="fw-600 fz-18">
        <FormattedMessage id="payment_info.title" />
      </span>
      <div className="mt-15">
        <Radio.Group
          value={paymentType}
          onChange={onPaymentChange}
          disabled={disabled}
        >
          <Radio value={CAMPAIGN_PAYMENT_TYPE.ACCOUNT_BALANCE}>
            <FormattedMessage id="payment_info.wallet" />
          </Radio>
          {allowCard && (
            <Radio value={CAMPAIGN_PAYMENT_TYPE.BANK}>
              <FormattedMessage id="payment_info.ach_bank_debit" />
            </Radio>
          )}
          {allowCard && (
            <Radio className="mt-10" value={CAMPAIGN_PAYMENT_TYPE.CREDIT_CARD}>
              <FormattedMessage id="payment_info.credit_card" />
              {transactionType !== TRANSACTION_TYPE.TEMPLATE_PAYMENT && (
                <FormattedMessage id="payment_info.credit_card_surcharge" />
              )}
            </Radio>
          )}
          {transactionType !== TRANSACTION_TYPE.TEMPLATE_PAYMENT &&
            transactionType !== TRANSACTION_TYPE.CAMPAIGN_PAYMENT && (
              <Radio
                className="mt-10"
                value={CAMPAIGN_PAYMENT_TYPE.WIRE_TRANSFER}
              >
                <FormattedMessage id="payment_info.wire_transfer" />
              </Radio>
            )}
            {
              userInfo?.role === USER_ROLE_TYPES.AGENCY &&
              transactionType === TRANSACTION_TYPE.CAMPAIGN_PAYMENT &&  (
                <Tooltip title={termQualification?.status !== QUALIFICATION_STATUS.ACTIVE 
                  && termQualification?.status !== QUALIFICATION_STATUS.REVIEW_LIMIT_INCREASE
                  ? 
                  'You are not eligible for Net Terms' 
                    : (termQualification?.availableCredit < calculateTotalPaymentWithTermsSurcharge(totalPayment) 
                      ? 'Your available credit is insufficient' 
                        : '')}>
                  <Radio
                    className="mt-10"
                    value={CAMPAIGN_PAYMENT_TYPE.TERM}
                    disabled={(termQualification?.status !== QUALIFICATION_STATUS.ACTIVE
                      && termQualification?.status !== QUALIFICATION_STATUS.REVIEW_LIMIT_INCREASE)
                      || termQualification?.availableCredit < calculateTotalPaymentWithTermsSurcharge(totalPayment)}
                  >
                    Net Terms(Due in 30 days) 
                  </Radio>
                </Tooltip>
            )}
        </Radio.Group>
      </div>

      {
        userInfo?.role === USER_ROLE_TYPES.AGENCY &&
          transactionType === TRANSACTION_TYPE.CAMPAIGN_PAYMENT && 
          (
           termQualification?.status === QUALIFICATION_STATUS.ACTIVE
           || termQualification?.status === QUALIFICATION_STATUS.REVIEW_LIMIT_INCREASE  ? 
            null : (
              <div className="mt-16">
                {
                  !termQualification?.status 
                    ? termQualificationLink && <>
                        {'Pay with Net Terms: '} <a href={termQualificationLink?.link} target='_blank'>Register here</a> 
                    </>
                    : <> {'Line of Credit Financing: '}{QUALIFICATION_STATUS_LABEL[termQualification?.status]}</> 
                }
              </div>
            )
        )}

      <div className="mt-32">
        <div>
          {paymentType === CAMPAIGN_PAYMENT_TYPE.ACCOUNT_BALANCE && (
            <span className="fz-12">
              <FormattedMessage id="payment_info.account_balance" />
            </span>
          )}
        </div>
        <div>
          {paymentType === CAMPAIGN_PAYMENT_TYPE.ACCOUNT_BALANCE ? (
            <MyInput
              className="account-balance"
              readOnly
              value={formatNumber(userInfo?.accountBalance)}
              suffix={
                allowCard ? (
                  <MyButton
                    type="primary"
                    className="light-blue fw-400"
                    onClick={onToggleTopup(true)}
                    disabled={disabled}
                  >
                    <FormattedMessage id="payment_info.add_fund" />
                  </MyButton>
                ) : canRequestFunds ? (
                  <MyButton
                    type="primary"
                    className="light-blue fw-400"
                    onClick={onToggleRequestFunds(true)}
                    disabled={disabled}
                  >
                    <FormattedMessage id="payment_info.request_funds" />
                  </MyButton>
                ) : null
              }
            />
          ) : (
            <></>
          )}
        </div>
      </div>

      <div className="mt-32">
        <div>
          {transactionType == TRANSACTION_TYPE.TEMPLATE_PAYMENT ? (
            <span className="fz-12">
              <FormattedMessage id="payment_info.discount_code" />
            </span>
          ) : (
            <span className="fz-12">
              <FormattedMessage id="payment_info.voucher_code" />
            </span>
          )}
        </div>
        <div>
          <MyInput
            targetRef={codeRef}
            suffix={
              codeData?.code ? (
                <MyButton
                  type="primary"
                  className="light-blue fw-400"
                  onClick={handleClickRemove}
                >
                  <FormattedMessage id="payment_info.remove" />
                </MyButton>
              ) : (
                <MyButton
                  type="primary"
                  className="light-blue fw-400"
                  onClick={handleClickApply}
                >
                  <FormattedMessage id="payment_info.apply" />
                </MyButton>
              )
            }
            placeholder={
              transactionType == TRANSACTION_TYPE.TEMPLATE_PAYMENT
                ? intl.formatMessage({ id: 'payment_info.enter_discount_code' })
                : intl.formatMessage({ id: 'payment_info.enter_voucher_code' })
            }
            autoComplete="off"
            defaultValue={codeData?.code}
          />
        </div>
      </div>

      {codeError ? (
        <AppliedCodeSection codeError={codeError} />
      ) : codeData ? (
        <AppliedCodeSection
          codeType={codeData.type}
          codeValue={codeData.value}
        />
      ) : null}

      <ModalTopUp
        visible={visibleTopup}
        onClose={onToggleTopup(false)}
        onOpenModalNewCard={onToggleNewCard(true)}
        checkout={balanceCheckout}
        form={formTopup}
      />
      <ModalNewCard visible={visibleNewCard} onClose={onToggleNewCard(false)} />
      <BalanceModal
        showModal={balanceCheckout.visible}
        type={BALANCE_MODAL_TYPE.BALANCE_CHECKOUT_TOPUP}
      />
      <ModalRequestFund
        visible={visibleRequestFunds}
        onClose={onToggleRequestFunds(false)}
      />
    </div>
  );
};

export default PaymentInfo;
